import React, { useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { authApi } from '../services/api';
import LanguageSwitcher from './LanguageSwitcher';
import { useLanguage } from '../contexts/LanguageContext';
import { DocumentProcessingIndicator } from './DocumentProcessingIndicator';

const Navbar: React.FC = () => {
  const location = useLocation();
  const user = authApi.getCurrentUser();
  const { t } = useLanguage();
  const navbarCollapseRef = useRef<HTMLDivElement>(null);
  const navbarTogglerRef = useRef<HTMLButtonElement>(null);

  const handleLogout = () => {
    authApi.logout();
  };

  const isActive = (path: string) => {
    return location.pathname === path ? 'nav-link active' : 'nav-link';
  };

  // Close navbar collapse on mobile when a link is clicked
  const closeNavbar = () => {
    if (navbarCollapseRef.current?.classList.contains('show')) {
      navbarTogglerRef.current?.click();
    }
  };

  return (
    <>
      <nav className="navbar navbar-expand-lg navbar-dark bg-primary">
        <div className="container-fluid">
          <Link className="navbar-brand" to="/dashboard" onClick={closeNavbar}>
            TPS Logistics
          </Link>
      
          <button
            ref={navbarTogglerRef}
            className="navbar-toggler"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#navbarNav"
          >
            <span className="navbar-toggler-icon"></span>
          </button>
          
          <div className="collapse navbar-collapse" id="navbarNav" ref={navbarCollapseRef}>
            <ul className="navbar-nav me-auto">
              <li className="nav-item">
                <Link className={isActive('/dashboard')} to="/dashboard" onClick={closeNavbar}>
                  {t('dashboard')}
                </Link>
              </li>
              <li className="nav-item">
                <Link className={isActive('/shipping')} to="/shipping" onClick={closeNavbar}>
                  {t('shipping.records')}
                </Link>
              </li>
              <li className="nav-item">
                <Link className={isActive('/shipping/cost-analysis')} to="/shipping/cost-analysis" onClick={closeNavbar}>
                  {t('cost.analysis')}
                </Link>
              </li>
              <li className="nav-item">
                <Link className={isActive('/repair')} to="/repair" onClick={closeNavbar}>
                  {t('repair.records')}
                </Link>
              </li>
              <li className="nav-item">
                <Link className={isActive('/prepaid')} to="/prepaid" onClick={closeNavbar}>
                  {t('prepaid.records')}
                </Link>
              </li>
              <li className="nav-item">
                <Link className={isActive('/vehicles')} to="/vehicles" onClick={closeNavbar}>
                  {t('vehicles')}
                </Link>
              </li>
              <li className="nav-item">
                <Link className={isActive('/vehicle-tracking')} to="/vehicle-tracking" onClick={closeNavbar}>
                  {t('vehicle.tracking')}
                </Link>
              </li>
      
              <li className="nav-item">
                <Link className={isActive('/drivers')} to="/drivers" onClick={closeNavbar}>
                  {t('drivers')}
                </Link>
              </li>
              <li className="nav-item">
                <Link className={isActive('/customers')} to="/customers" onClick={closeNavbar}>
                  {t('customer')}
                </Link>
              </li>
              <li className="nav-item">
                <Link className={isActive('/fuel-receipts')} to="/fuel-receipts" onClick={closeNavbar}>
                  {t('fuel.receipt.management')}
                </Link>
              </li>
              <li className="nav-item">
                <Link className={isActive('/document-upload')} to="/document-upload" onClick={closeNavbar}>
                  {t('document.upload')}
                </Link>
              </li>
              <li className="nav-item">
                <Link className={isActive('/document-upload/history')} to="/document-upload/history" onClick={closeNavbar}>
                  {t('processing.history') || 'Processing History'}
                </Link>
              </li>
      
              {/* Pricing Dropdown - Simplified */}
       <li className="nav-item">
    <Link className={isActive('/pricing/fee-structure')} to="/pricing/fee-structure" onClick={closeNavbar}>
   {t('fee.structure.management') || 'Fee Management'}
 </Link>
      </li>
         
 {authApi.hasRole('Admin') && (
                <li className="nav-item">
                  <Link className={isActive('/users')} to="/users" onClick={closeNavbar}>
                    {t('users')}
                  </Link>
                </li>
              )}
              <li className="nav-item dropdown">
                <a
                  className="nav-link dropdown-toggle"
                  href="#system"
                  role="button"
                  data-bs-toggle="dropdown"
                >
                  {t('system')}
                </a>
                <ul className="dropdown-menu">
                  <li>
                    <Link className="dropdown-item" to="/settings" onClick={closeNavbar}>
                      {t('settings')}
                    </Link>
                  </li>
                  <li>
                    <Link className="dropdown-item" to="/vehicles/capacity-tons" onClick={closeNavbar}>
                      {t('vehicle.capacity.tons') || 'Vehicle Capacities'}
                    </Link>
                  </li>
                  {authApi.hasRole('Admin') && (
                    <li>
                      <Link className="dropdown-item" to="/shipping/deleted" onClick={closeNavbar}>
                        {t('deleted.records')}
                      </Link>
                    </li>
                  )}
                </ul>
              </li>
            </ul>
            
            <ul className="navbar-nav">
              <li className="nav-item">
                <LanguageSwitcher />
              </li>
              <li className="nav-item dropdown">
                <a
                  className="nav-link dropdown-toggle"
                  href="#dropdown"
                  role="button"
                  data-bs-toggle="dropdown"
                >
                  {user?.firstName || user?.userName}
                </a>
                <ul className="dropdown-menu dropdown-menu-end">
                  <li>
                    <span className="dropdown-item-text">
                      {user?.roles.join(', ')}
                    </span>
                  </li>
                  <li><hr className="dropdown-divider" /></li>
                  <li>
                    <Link className="dropdown-item" to="/change-password" onClick={closeNavbar}>
                      Change Password
                    </Link>
                  </li>
                  <li>
                    <button className="dropdown-item" onClick={handleLogout}>
                      {t('logout')}
                    </button>
                  </li>
                </ul>
              </li>
            </ul>
          </div>
        </div>
      </nav>
      
      {/* Document Processing Indicator - Shows active jobs across all pages */}
      <DocumentProcessingIndicator />
    </>
  );
};

export default Navbar;